/**
 * lazyPage — React.lazy for a page, safe across deploys and while offline.
 *
 * Staff pages load in their own chunks, so customers don't download them. That adds two
 * ways for a page to fail to load, and both are handled here:
 * - After a deploy, the old chunk files are gone. A tab still on the old build that opens a
 *   page it hasn't loaded asks for a missing file. Reloading gets the new build, so the
 *   first such failure reloads the page. A session flag allows one reload, so a real outage
 *   shows PageBoundary's message instead of reloading forever.
 * - Offline (the truck tablet), the chunk can't be fetched. The page stays on "Loading…" and
 *   loads once the connection is back. A reload here would show the browser's offline page.
 *   Staff devices also fetch every page after sign-in (`preload`), so this is rare.
 */
import { lazy } from 'react';
import { reloadPage } from './appVersion';

const RELOAD_FLAG = 'app_chunk_reload';

/** webpack's "Loading chunk 12 failed" (script) and "Loading CSS chunk 12 failed" (styles). */
export function isChunkError(err) {
  return err?.name === 'ChunkLoadError'
    || err?.code === 'CSS_CHUNK_LOAD_FAILED'
    || /Loading (CSS )?chunk \S+ failed/i.test(err?.message || '');
}

/** Reload once per tab session. False when it already has, or can't keep count. */
function reloadOnce() {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    return false; // storage blocked: nothing would stop a reload loop, so show the message instead
  }
  reloadPage();
  return true;
}

function clearReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // storage blocked: the flag was never written either
  }
}

const offline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
const backOnline = () => new Promise((resolve) => { window.addEventListener('online', resolve, { once: true }); });
const forever = () => new Promise(() => {});

async function loadPage(load) {
  for (;;) {
    try {
      const page = await load();
      clearReloadFlag();
      return page;
    } catch (err) {
      if (!isChunkError(err)) throw err;
      if (offline()) await backOnline(); // webpack fetches a failed chunk again on the next import()
      else if (reloadOnce()) return forever(); // keep "Loading…" up until the page reloads
      else throw err;
    }
  }
}

/** A lazy page component. `Page.preload()` fetches its code ahead of time. */
export function lazyPage(load) {
  const Page = lazy(() => loadPage(load));
  Page.preload = load;
  return Page;
}
